import { Building2, Eye, MoreVertical, SearchX } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";
import { formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import { WALLETS_OVERVIEW_PAGE_SIZE } from "../../hooks/useWalletsOverview";
import type { RuntimeWallet, RuntimeWalletStatus } from "../../types/payments-wallets.types";
import { overviewFractionDigits, shareOf, walletHeld } from "../../utils/walletsOverview";
import { CurrencyMark } from "./CurrencyMark";

export interface OverviewOrganization {
  id: string;
  name: string;
}

export interface WalletsOverviewTableProps {
  rows: RuntimeWallet[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  /** `held` per currency from the summary, for each wallet's share of its currency. */
  heldByCurrency: ReadonlyMap<string, string>;
  masked: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onOpenOrganization: (organization: OverviewOrganization) => void;
}

const STATUS_BADGE: Record<RuntimeWalletStatus, "success" | "warning" | "secondary"> = {
  active: "success",
  frozen: "warning",
  closed: "secondary",
};

/**
 * Every wallet in scope, one row each, ranked server-side. A table from `xl` up — below that the page cannot
 * give it its width once the sidebar takes its share — and cards under it.
 */
export function WalletsOverviewTable({
  rows,
  total,
  page,
  onPageChange,
  isLoading,
  heldByCurrency,
  masked,
  hasActiveFilters,
  onClearFilters,
  onOpenOrganization,
}: WalletsOverviewTableProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const navigate = useNavigate();
  const openMovements = (wallet: RuntimeWallet) => navigate(`/app/wallets/${wallet.id}/movements`);
  const percent = (value: number) =>
    new Intl.NumberFormat(activeLanguage, {
      style: "percent",
      maximumFractionDigits: value < 0.1 ? 1 : 0,
    }).format(value);

  const amount = (wallet: RuntimeWallet, value: string, emphasis = false) => (
    <ColocaAmountDisplay
      value={value}
      currency={emphasis ? wallet.currency : undefined}
      fractionDigits={overviewFractionDigits(wallet.currency)}
      masked={masked}
      size={emphasis ? "sm" : "xs"}
      className={emphasis ? "text-foreground" : "text-muted-foreground"}
    />
  );

  const organization = (wallet: RuntimeWallet) => (
    <button
      type="button"
      onClick={() =>
        onOpenOrganization({ id: wallet.organization_id, name: wallet.organization?.name ?? "" })
      }
      className="block max-w-56 truncate text-left text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
      title={wallet.organization?.name}
    >
      {wallet.organization?.name || wallet.organization_id}
    </button>
  );

  const share = (wallet: RuntimeWallet) => {
    const value = shareOf(walletHeld(wallet), heldByCurrency.get(wallet.currency));
    if (value === null) return <span className="text-xs text-muted-foreground">—</span>;
    return (
      <span className="flex items-center gap-2">
        <ColocaProgress value={value * 100} size="sm" tone="primary" className="w-16" aria-hidden />
        <span className="text-xs text-muted-foreground tabular-nums">{percent(value)}</span>
      </span>
    );
  };

  const status = (wallet: RuntimeWallet) => (
    <ColocaBadge variant={STATUS_BADGE[wallet.status]} className="text-[10px]">
      {t(`status_${wallet.status}`)}
    </ColocaBadge>
  );

  const lastMovement = (wallet: RuntimeWallet) =>
    wallet.last_movement_at ? (
      <span className="text-xs text-muted-foreground">
        {formatTimeAgo(wallet.last_movement_at, { locale: activeLanguage })}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground/70">{t("never_moved")}</span>
    );

  const actions = (wallet: RuntimeWallet) => (
    <ColocaDropdownMenu
      trigger={
        <ColocaIconButton icon={MoreVertical} variant="ghost" size="sm" aria-label={t("col_actions")} />
      }
      items={[
        { label: t("card_menu_movements"), icon: Eye, onClick: () => openMovements(wallet) },
        {
          label: t("action_open_org"),
          icon: Building2,
          onClick: () =>
            onOpenOrganization({ id: wallet.organization_id, name: wallet.organization?.name ?? "" }),
        },
      ]}
    />
  );

  const columns: ColocaDataTableColumn<RuntimeWallet>[] = [
    { id: "organization", header: t("col_organization"), cell: organization, cellClassName: "max-w-56" },
    { id: "currency", header: t("col_currency"), cell: (w) => <CurrencyMark currency={w.currency} /> },
    {
      id: "held",
      header: t("col_held"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (w) => (
        <span className="flex flex-col items-end gap-0.5">
          {amount(w, walletHeld(w), true)}
          <span className="flex gap-2 text-[11px] text-muted-foreground">
            <span>
              {t("summary_available")} {amount(w, w.balance_available)}
            </span>
            {Number(w.balance_blocked) > 0 && (
              <span>
                {t("summary_blocked")} {amount(w, w.balance_blocked)}
              </span>
            )}
          </span>
        </span>
      ),
    },
    { id: "share", header: t("col_share"), cell: share },
    {
      id: "pending",
      header: t("summary_pending"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (w) => amount(w, w.balance_pending),
    },
    { id: "status", header: t("col_status"), cell: status },
    { id: "last_movement", header: t("col_last_movement"), cell: lastMovement },
    {
      id: "actions",
      header: <span className="sr-only">{t("col_actions")}</span>,
      cellClassName: "w-12 text-right",
      cell: actions,
    },
  ];

  const empty = (
    <ColocaEmpty
      surface="card"
      icon={SearchX}
      title={t("table_empty")}
      description={hasActiveFilters ? t("table_empty_desc") : undefined}
    >
      {hasActiveFilters && (
        <ColocaButton variant="secondary" size="sm" onClick={onClearFilters}>
          {t("filter_clear")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const pagination =
    total > WALLETS_OVERVIEW_PAGE_SIZE ? (
      <ColocaPagination
        page={page}
        perPage={WALLETS_OVERVIEW_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
        withSeparator={false}
        size="sm"
        summary={t("table_total", { total: new Intl.NumberFormat(activeLanguage).format(total) })}
      />
    ) : undefined;

  return (
    <>
      <div className="hidden xl:block">
        <ColocaDataTable
          columns={columns}
          rows={rows}
          rowKey={(wallet) => wallet.id}
          onRowClick={openMovements}
          loading={isLoading && rows.length === 0}
          minWidth={1040}
          empty={empty}
          footer={pagination}
          className={isLoading && rows.length > 0 ? "opacity-60 transition-opacity" : undefined}
        />
      </div>

      <div className="space-y-3 xl:hidden">
        {isLoading && rows.length === 0 ? (
          Array.from({ length: 3 }, (_, index) => <ColocaSkeleton key={index} className="h-36 rounded-xl" />)
        ) : rows.length === 0 ? (
          empty
        ) : (
          <>
            <ul className="space-y-3">
              {rows.map((wallet) => (
                <li key={wallet.id} className="space-y-3 rounded-xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      {organization(wallet)}
                      <CurrencyMark currency={wallet.currency} />
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {status(wallet)}
                      {actions(wallet)}
                    </div>
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="text-[11px] text-muted-foreground">{t("col_held")}</p>
                      {amount(wallet, walletHeld(wallet), true)}
                    </div>
                    {share(wallet)}
                  </div>
                  <dl className="grid grid-cols-3 gap-2 border-t border-border pt-3 text-[11px] text-muted-foreground">
                    <div>
                      <dt>{t("summary_available")}</dt>
                      <dd>{amount(wallet, wallet.balance_available)}</dd>
                    </div>
                    <div>
                      <dt>{t("summary_blocked")}</dt>
                      <dd>{amount(wallet, wallet.balance_blocked)}</dd>
                    </div>
                    <div>
                      <dt>{t("summary_pending")}</dt>
                      <dd>{amount(wallet, wallet.balance_pending)}</dd>
                    </div>
                  </dl>
                  <div className="flex items-center justify-between gap-3">
                    {lastMovement(wallet)}
                    <ColocaButton variant="ghost" size="sm" icon={Eye} onClick={() => openMovements(wallet)}>
                      {t("card_menu_movements")}
                    </ColocaButton>
                  </div>
                </li>
              ))}
            </ul>
            {pagination}
          </>
        )}
      </div>
    </>
  );
}
